import { useState } from "react";
import { Link } from "react-router-dom";

import {
  DEFAULT_PACK_MIB,
  ENGINE_CEILING,
  MAX_PACK_MIB,
  MIN_PACK_MIB,
  THREAD_LIMITS,
  TRANSFER_MECHANICS,
  backupMemoryMiB,
  clampPack,
  clampWorkers,
  effectiveMax,
  formatMiB,
  restoreMemoryMiB,
} from "./providers/tuning";
import { PRICES_CHECKED, PROVIDER_PRICES, WORKED_EXAMPLES } from "./providers/pricing";

interface Section {
  id: string;
  title: string;
}

const SECTIONS: Section[] = [
  { id: "classes", title: "Provider classes" },
  { id: "pricing", title: "Pricing" },
  { id: "threads", title: "Threads" },
  { id: "pack-size", title: "Pack size" },
  { id: "tuning", title: "Tuning" },
  { id: "memory", title: "Memory" },
];

export function Providers() {
  return (
    <>
      <section className="page-header">
        <h1>Cloud providers, threads and pack size</h1>
        <p className="muted">
          What each kind of storage costs, why the default thread counts differ between them, and
          how to tune both on purpose instead of by guessing.
        </p>
      </section>

      <nav className="features-jump">
        {SECTIONS.map((s) => (
          <a key={s.id} href={`#${s.id}`}>
            {s.title}
          </a>
        ))}
      </nav>

      <ProviderClasses />
      <Pricing />
      <Threads />
      <PackSize />
      <Tuning />
      <Memory />

      <p className="muted">
        Want the byte layout a pack actually has? <Link to="/spec">Format spec, for nerds.</Link>
      </p>
    </>
  );
}

function ProviderClasses() {
  return (
    <section className="panel features-group" id="classes">
      <h2>Provider classes</h2>
      <p className="muted">
        Every destination this app supports falls into one of three classes, and the class — not
        the brand — is what predicts how it behaves under load and what it costs to use.
      </p>

      <h3>Sync providers — Google Drive, OneDrive, Dropbox</h3>
      <p>
        A flat monthly price for a fixed amount of space, usually space you already pay for
        anyway. Downloading is free. In exchange, each account is metered: a request quota, a
        daily upload cap, or — on Dropbox — a lock on writes to a folder. These three behave more
        alike than they differ, which is why they're grouped: none of them wants an unbounded
        number of simultaneous requests, and all three tell you so with a 429 rather than simply
        failing. They suit a vault sized to what you'd comfortably keep in that account.
      </p>

      <h3>Forgiving object stores — Backblaze B2, Wasabi (and Cloudflare R2)</h3>
      <p>
        Pay per terabyte stored, with no separate account tier to outgrow. Backblaze B2 gives back
        free egress up to three times what you store each month — enough to restore everything at
        least once and still owe nothing for it — and charges for space, not for asking. Wasabi
        charges no egress at all, in exchange for a one-terabyte minimum bill and a 90-day minimum
        retention per object. Both are built to take many requests at once; there is no folder lock
        to queue behind. This is the class most vaults should default to.
      </p>

      <h3>Hyperscalers — AWS, Google Cloud, Azure</h3>
      <p>
        Uploading is always free on all three. Storing a terabyte in a deep archive tier costs
        about a dollar a month — Amazon's Glacier Deep Archive is priced for exactly that. The
        expensive direction is the other one: reading data back out, priced as data-transfer{" "}
        <em>egress</em>, runs on the order of $90 per terabyte on top of a per-gigabyte retrieval
        fee and, for the coldest tiers, a wait of hours before the data is even readable again.
        Treat this class as the "oh crap" copy you hope never to open, not the copy you restore a
        folder from on a Tuesday. A minimum storage duration (90 to 365 days depending on tier and
        provider) also means deleting early can cost more than keeping it.
      </p>
    </section>
  );
}

function Pricing() {
  return (
    <section className="panel features-group" id="pricing">
      <h2>Pricing</h2>
      <p className="muted">
        List prices, rounded to the dollar except where a provider prices in cents, checked{" "}
        {PRICES_CHECKED} unless a row says otherwise. These move — check the provider's own page
        before deciding, and treat this table as the shape of the bill rather than a quote.
      </p>

      <div className="table-scroll">
        <table className="runs runs--prose">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Plan</th>
              <th>≈ $/TB/mo storage</th>
              <th>Egress (restoring)</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {PROVIDER_PRICES.map((p) => (
              <tr key={p.id}>
                <td>{p.provider}</td>
                <td>{p.plan}</td>
                <td>${p.storagePerTbMonth}</td>
                <td>{p.egress}</td>
                <td className="muted">
                  {p.notes}
                  {p.checked ? ` · checked ${p.checked}` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>Worked example: store 1 TB for a year, restore it once</h3>
      <div className="table-scroll">
        <table className="runs">
          <thead>
            <tr>
              <th>Destination</th>
              <th>Approximate total</th>
            </tr>
          </thead>
          <tbody>
            {WORKED_EXAMPLES.map((w) => (
              <tr key={w.id}>
                <td>{w.label}</td>
                <td>{w.costLabel}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted">
        Storage price alone makes a deep-archive tier look like the cheapest place on the list.
        The restore column is why it isn't the whole story: on a hyperscaler, reading the data back
        can cost more than a year of storing it did.
      </p>
    </section>
  );
}

function Threads() {
  return (
    <section className="panel features-group" id="threads">
      <h2>Threads</h2>
      <p className="muted">
        Every destination gets its own default and ceiling for upload and restore concurrency. Two
        numbers decide what a run actually uses: the engine's own ceiling of {ENGINE_CEILING}{" "}
        workers, which is about memory in this process, and the destination's own limit, which is
        about how hard that service wants to be asked. A run gets the lower of the two.
      </p>

      <div className="table-scroll">
        <table className="runs">
          <thead>
            <tr>
              <th>Destination</th>
              <th>Upload default</th>
              <th>Upload max</th>
              <th>Restore default</th>
              <th>Restore max</th>
            </tr>
          </thead>
          <tbody>
            {THREAD_LIMITS.map((t) => (
              <tr key={t.id}>
                <td>{t.destination}</td>
                <td>{t.uploadDefault}</td>
                <td>{effectiveMax(t.uploadMax)}</td>
                <td>{t.restoreDefault}</td>
                <td>{effectiveMax(t.restoreMax)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted">
        An object store will tolerate far more than 16 at once; the number shown here is the
        engine's own ceiling, since that's what a run will actually be capped at.
      </p>

      <h3>Why each one is set where it is</h3>
      <ul>
        {THREAD_LIMITS.map((t) => (
          <li key={t.id}>
            <strong>{t.destination}.</strong> {t.why}
          </li>
        ))}
      </ul>

      <h3>How one pack actually travels</h3>
      <p className="muted">
        The thread count decides how many packs move at once; this decides what "moving" means
        for a single one of them.
      </p>
      <div className="table-scroll">
        <table className="runs runs--prose">
          <thead>
            <tr>
              <th>Destination</th>
              <th>Upload mechanism</th>
              <th>Switches at</th>
            </tr>
          </thead>
          <tbody>
            {TRANSFER_MECHANICS.map((m) => (
              <tr key={m.destination}>
                <td>{m.destination}</td>
                <td className="muted">{m.upload}</td>
                <td>{m.threshold}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>What happens when a destination pushes back</h3>
      <p>
        Every request goes through a shared throttle gate. The first worker to get a 429 or 503
        pauses every worker for the length of time the server asked for, so the rest of the pool
        doesn't retry in lockstep and re-trigger the same throttle a moment later. Waits are
        jittered — spread wider when the server gave a hint, spread across the middle of the
        backoff window when it didn't — so workers don't all wake up at once either. A run that
        starts too wide for a destination corrects itself rather than failing outright.
      </p>
    </section>
  );
}

function PackSize() {
  return (
    <section className="panel features-group" id="pack-size">
      <h2>Pack size</h2>
      <p>
        A pack is the sealed, encrypted container that chunks get written into before upload —
        the unit the app actually stores and reads back, described byte-for-byte in the{" "}
        <Link to="/spec">format spec</Link>. Its target size is a per-storage-location setting,
        from {MIN_PACK_MIB} MiB up to {MAX_PACK_MIB} MiB, defaulting to {DEFAULT_PACK_MIB} MiB.
      </p>

      <h3>Can I change it after I've already backed up?</h3>
      <p>
        Yes, at any time, with no ill effects on what's already stored. Pack size is a decision the
        writer makes each time it seals a pack; it isn't recorded in the vault at all, so
        existing packs stay exactly as readable as they were, whatever size they happened to be
        sealed at. A vault can hold a mixture of pack sizes and nothing downstream — restore,
        verify, prune, gc — cares. The only guard is in the app itself: the pack-size field is
        locked while a backup or restore is running against that location, so the safe sequence is
        simply to let the current run finish (or stop it), change the setting, and start the next
        one. There's no migration step and nothing to re-upload.
      </p>

      <h3>What goes wrong if it's set badly</h3>
      <p>
        Too small, and every gigabyte turns into more objects: more list-and-verify overhead, more
        requests (which costs money on a per-request-priced hyperscaler and burns quota faster on a
        sync provider), and on Dropbox specifically, more uploads landing under its per-folder write
        lock instead of going as sessions. {MIN_PACK_MIB} MiB is the floor for a reason — below it,
        the per-pack overhead stops being worth paying. Too large, and the cost moves to memory (see
        below): a backend that uploads a pack as a single request restarts that request from zero on
        failure, and garbage collection reclaims space one whole pack at a time, so a giant pack with
        one still-referenced chunk in it stays on disk. Nothing about a too-large pack corrupts data
        or loses anything — it's slower and hungrier, not unsafe.
      </p>
    </section>
  );
}

function Tuning() {
  return (
    <section className="panel features-group" id="tuning">
      <h2>Tuning</h2>
      <p>
        {DEFAULT_PACK_MIB} MiB packs at the default thread count is a good starting point for
        nearly every destination, including on a gigabit connection. A TCP connection's throughput
        is roughly its window divided by round-trip time, so as ping to a destination rises, the
        fix is more simultaneous connections rather than bigger requests — which is exactly what
        raising the thread count buys. At typical cloud round-trip times, four to eight requests in
        flight together are enough to keep a gigabit link busy while each request stays long enough
        to be worth its own connection setup and authentication overhead.
      </p>
      <p>
        Pack size matters for a different reason: it decides how many requests a given amount of
        data turns into. Raise it when the bottleneck is request count rather than bandwidth —
        Dropbox crossing its 140 MiB session threshold to stop queuing on the folder lock, a sync
        provider's per-minute quota, or a hyperscaler charging per request — or when a dataset is
        large enough that fewer, bigger packs meaningfully cut down on listing and bookkeeping work.
        128 MiB and up is worth trying on Dropbox for a backup made of many small uploads, and on
        any destination where the "destination pressure" report (logged after every run) shows time
        going into waiting on requests rather than moving bytes.
      </p>
      <p className="muted">
        In short: change one setting at a time, measure with that report before changing the other,
        raise thread count first for a high-latency link, and raise pack size first for Dropbox or
        when request counts are what's costing time or money.
      </p>

      <div className="table-scroll">
        <table className="runs runs--prose">
          <thead>
            <tr>
              <th>Situation</th>
              <th>Reasonable starting point</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Most destinations, most datasets</td>
              <td>64 MiB packs, the destination's own default thread count</td>
            </tr>
            <tr>
              <td>Local backup to a spinning hard drive</td>
              <td>1–2 threads — extra concurrency just makes the drive seek</td>
            </tr>
            <tr>
              <td>Local backup to an SSD or NVMe</td>
              <td>4–8 threads, 64 MiB packs</td>
            </tr>
            <tr>
              <td>Dropbox, many small files</td>
              <td>4 threads, and try 128–256 MiB packs to clear the session threshold</td>
            </tr>
            <tr>
              <td>High round-trip time to any cloud destination</td>
              <td>Raise threads toward the destination's max before touching pack size</td>
            </tr>
            <tr>
              <td>Machine with under 8 GiB of RAM</td>
              <td>Don't raise threads and pack size at the same time — see Memory below</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Memory() {
  const [workers, setWorkers] = useState(4);
  const [packMiB, setPackMiB] = useState(DEFAULT_PACK_MIB);

  const w = clampWorkers(workers);
  const pack = clampPack(packMiB);
  const backup = backupMemoryMiB(w, pack);
  const restore = restoreMemoryMiB(w, pack);

  return (
    <section className="panel features-group" id="memory">
      <h2>Memory</h2>
      <p>
        Both operations buffer whole packs, not just chunks, because a pack is sealed and read as
        one unit. Backup memory is roughly the thread count times the pack size, briefly up to
        twice that while a pack is being sealed; restore memory is roughly one pack per worker plus
        one for the worker feeding them. Raising threads and pack size at the same time multiplies
        rather than adds, which is the combination most likely to run a small machine out of
        memory.
      </p>

      <div className="estimator">
        <div className="field">
          <label htmlFor="providers-threads">Worker threads</label>
          <input
            id="providers-threads"
            type="number"
            min={1}
            max={ENGINE_CEILING}
            value={workers}
            onChange={(e) => {
              if (e.target.value !== "") setWorkers(Number(e.target.value));
            }}
          />
        </div>
        <div className="field">
          <label htmlFor="providers-pack">Pack size (MiB)</label>
          <input
            id="providers-pack"
            type="number"
            min={MIN_PACK_MIB}
            max={MAX_PACK_MIB}
            step={16}
            value={packMiB}
            onChange={(e) => {
              if (e.target.value !== "") setPackMiB(Number(e.target.value));
            }}
          />
        </div>
      </div>

      <div className="table-scroll">
        <table className="runs" data-testid="memory-estimate">
          <thead>
            <tr>
              <th>Operation</th>
              <th>Steady</th>
              <th>Peak</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Backup ({w} threads, {pack} MiB packs, 5% parity)</td>
              <td>{formatMiB(backup.steady)}</td>
              <td>{formatMiB(backup.peak)}</td>
            </tr>
            <tr>
              <td>Restore / verify ({w} threads, {pack} MiB packs)</td>
              <td>{formatMiB(restore)}</td>
              <td>{formatMiB(restore)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>Common combinations</h3>
      <div className="table-scroll">
        <table className="runs">
          <thead>
            <tr>
              <th>Threads</th>
              <th>Pack size</th>
              <th>Backup peak</th>
              <th>Restore</th>
            </tr>
          </thead>
          <tbody>
            {[4, 8, 16].flatMap((wk) =>
              [64, 128, 1024].map((pk) => {
                const b = backupMemoryMiB(wk, pk);
                const r = restoreMemoryMiB(wk, pk);
                return (
                  <tr key={`${wk}-${pk}`}>
                    <td>{wk}</td>
                    <td>{pk} MiB</td>
                    <td>{formatMiB(b.peak)}</td>
                    <td>{formatMiB(r)}</td>
                  </tr>
                );
              }),
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
